import React, { useState, useContext, useEffect } from "react";
import { css } from "@emotion/react";
import { Link } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";

import { UserContext } from "../context/user";

import { baseColor1, baseColor2, logoColor3 } from "../styles/color";
import { breakpointSm, breakpointMd } from "../styles/layout";

import MenuButton from "../images/menu-button.svg";
import CancelButton from "../images/cancel-button.svg";

import MulticolorLogo from "../images/one-work-multicolor-logo.svg";
import { AmplitudeContext } from "../context/amplitude";

const navStyle = css`
  position: relative;
  grid-column: 1 / -1;
  margin: 1rem auto;
  width: 100%;

  @media only screen and (min-width: ${breakpointMd}) {
    margin-top: 2rem;
  }
`;

const logoStyle = css`
  z-index: 2;
`;

const listStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  list-style-type: none;
  margin: 0 auto;
  padding: 0;
`;

const toggleStyle = css`
  display: initial;

  @media only screen and (min-width: ${breakpointMd}) {
    display: none;
  }
`;

const sideBySideMenuStyle = css`
  display: none;

  @media only screen and (min-width: ${breakpointMd}) {
    display: initial;

    ul {
      display: flex;
      align-items: center;
      list-style-type: none;
      font-size: 1rem;
      font-weight: 600;
      li:not(:first-of-type) {
        margin-left: 1.5rem;
      }
      a {
        color: ${baseColor1};
        text-decoration: none;
      }
    }

    #join-button-desktop {
      margin-left: 0.75rem;
    }
  }
`;

const menuStyle = css`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 100;
  width: calc(100vw - 2rem);
  height: 100vh;
  padding: 1rem;
  background-color: ${baseColor2};
  ul:first-of-type {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    list-style-type: none;
  }
  ul:nth-of-type(2) {
    margin-top: 3rem;
    font-size: 1rem;
    font-weight: 600;
    color: ${baseColor1};
    list-style-type: none;
    li {
      margin-bottom: 1rem;
      a {
        color: ${baseColor1};
        text-decoration: none;
      }
    }
  }
  .social {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    margin-top: 3rem;
    span {
      margin-right: 1rem;
    }
  }

  @media only screen and (min-width: ${breakpointSm}) {
    padding: 2rem;
    width: calc(100vw - 4rem);
  }
`;

const menuButtonStyle = css`
  border: none;
  background: none;
  padding: 0;
`;

const hideMenuStyle = css`
  display: none;
`;

const loginStyle = css`
  box-sizing: border-box;
  padding: 3px 12px;
  background-color: ${baseColor2};
  color: ${logoColor3};
  font-size: 1rem;
  border-radius: 6px;
  border: 1px solid ${logoColor3};
  cursor: pointer;
`;

const joinStyle = css`
  background-color: ${logoColor3};
  color: ${baseColor2};
`;

const mobileLoginStyle = css`
  margin: 2rem 0 0 0;
  width: 100%;
  padding: 7px 12px;
`;

const joinMobileStyle = css`
  ${joinStyle}
  margin-top: 0.75rem;
`;

const NavBar = () => {
  const user = useContext(UserContext);
  const monitor = useContext(AmplitudeContext);
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  useEffect(() => {
    if (user !== undefined && user !== null) {
      monitor.setUser(user.uid);
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMenuToggle = () => {
    setMenuIsOpen(!menuIsOpen);
  };

  return (
    <div className="material-section">
      <nav css={navStyle}>
        <ul css={listStyle}>
          <li css={logoStyle}>
            <Link to="/">
              <img
                src={MulticolorLogo}
                alt="the multicolor One Work logo"
                height={48}
              />
            </Link>
          </li>
          <li css={toggleStyle}>
            <button css={menuButtonStyle} onClick={handleMenuToggle}>
              <img src={MenuButton} alt="menu button" />
            </button>
          </li>
          <li css={sideBySideMenuStyle}>
            <ul>
              <li>
                <Link to="/mission-areas">Mission Areas</Link>
              </li>
              <li>
                <Link to="/about">About Us</Link>
              </li>
              <li>
                <a href="https://support.onework.co/">Support</a>
              </li>
              <li>
                <Link to="/blog">Blog</Link>
              </li>
              {user ? (
                <li>
                  <Link to="/app/">
                    <button css={[loginStyle, joinStyle]}>Go to app</button>
                  </Link>
                </li>
              ) : (
                <>
                  <li>
                    <Link to="/login">
                      <button css={loginStyle}>Log in</button>
                    </Link>
                  </li>
                </>
              )}
            </ul>
          </li>
        </ul>

        <div
          id="menu"
          css={menuIsOpen ? menuStyle : [menuStyle, hideMenuStyle]}
        >
          <ul>
            <li>
              <Link to="/" onClick={handleMenuToggle}>
                <img
                  src={MulticolorLogo}
                  alt="the multicolor One Work logo"
                  height={48}
                />
              </Link>
            </li>
            <li>
              <button css={menuButtonStyle} onClick={handleMenuToggle}>
                <img src={CancelButton} alt="close menu button" />
              </button>
            </li>
          </ul>

          <ul>
            <li>
              <Link to="/mission-areas" onClick={handleMenuToggle}>
                Mission Areas
              </Link>
            </li>
            <li>
              <Link to="/about" onClick={handleMenuToggle}>
                About Us
              </Link>
            </li>
            <li>
              <a href="https://support.onework.co/">Support</a>
            </li>
            <li>
              <Link to="/blog" onClick={handleMenuToggle}>
                Blog
              </Link>
            </li>
            {user ? (
              <li>
                <Link to="/app/">
                  <button css={[loginStyle, mobileLoginStyle, joinMobileStyle]}>
                    Go to app
                  </button>
                </Link>
              </li>
            ) : (
              <li>
                <Link to="/login">
                  <button css={[loginStyle, mobileLoginStyle]}>Log in</button>
                </Link>
              </li>
            )}
          </ul>

          <div className="social">
            <span>
              <a
                href="https://twitter.com/joinonework"
                target="_blank"
                rel="noopener noreferrer"
              >
                <StaticImage
                  src="../images/twitter-white.svg"
                  alt="Twitter logo"
                  width={24}
                  objectFit="contain"
                />
              </a>
            </span>
            <span>
              <a
                href="https://www.instagram.com/joinonework"
                target="_blank"
                rel="noopener noreferrer"
              >
                <StaticImage
                  src="../images/instagram-white.svg"
                  alt="Instagram logo"
                  width={24}
                />
              </a>
            </span>
            <span>
              <a
                href="https://www.facebook.com/joinonework"
                target="_blank"
                rel="noopener noreferrer"
              >
                <StaticImage
                  src="../images/facebook-white.svg"
                  alt="Facebook logo"
                  width={24}
                />
              </a>
            </span>
            <span>
              <a
                href="https://www.linkedin.com/company/joinonework"
                target="_blank"
                rel="noopener noreferrer"
              >
                <StaticImage
                  src="../images/linkedin-white.png"
                  alt="LinkedIn logo"
                  width={24}
                />
              </a>
            </span>
          </div>
        </div>
      </nav>
    </div>
  );
};

export default NavBar;
